import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { Button, Markdown, Modal, ModalBody, Text } from "@wojo/ui";
import pluralize from "pluralize";
import React, { useState } from "react";
import { FiInfo } from "react-icons/fi";
import { TourExtensionContent } from "../../../../generated/graphql";
import departureStyles from "../trip-builder-departure-step/TripBuilderDeparturesStep.module.scss";
import styles from "./TripBuilderQuoteStep.module.scss";

export type QuoteExtensionsModalProps = {
    content?: Partial<TourExtensionContent> | null;
    formattedPrice?: React.ReactNode;
    isIconTrigger?: boolean;
};

export const QuoteExtensionsModal: React.FC<QuoteExtensionsModalProps> = ({
    content,
    formattedPrice,
    isIconTrigger,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { productPage } = useI18n("productPage");

    if (!content || !content.name) {
        return null;
    }

    const { name, description, nbNights, image } = content;

    const title = productPage.tripBuilder.departures.modalTriggerLabel.l({
        subject: productPage.tripBuilder.departures.columnExtension.l(),
    });

    return (
        <>
            {isIconTrigger ? (
                <button
                    className={departureStyles["info-button"]}
                    type="button"
                    onClick={() => {
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_object: "TRIP_BUILDER",
                                ga4_action: "MODAL_OPENED",
                                modalId: "Extension Info",
                            },
                        });
                        setIsOpen(true);
                    }}
                >
                    <FiInfo size={16} title={title} aria-label={title} />
                </button>
            ) : (
                <Button
                    variant="quiet"
                    type="button"
                    onClick={() => {
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_object: "TRIP_BUILDER",
                                ga4_action: "MODAL_OPENED",
                                modalId: "Extension Info",
                            },
                        });
                        setIsOpen(true);
                    }}
                >
                    {productPage.tripBuilder.quote.extension.modalButtonLabel.l()}{" "}
                    <span className="visually-hidden">: {name}</span>
                </Button>
            )}

            <Modal
                onCloseRequested={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "MODAL_CLOSED",
                            modalId: "Extension Info",
                        },
                    });
                    setIsOpen(false);
                }}
                isOpen={isOpen}
                accessibleTitle={name}
            >
                <ModalBody style={{ paddingBottom: "var(--g-spacing-md)" }}>
                    <Text.Heading
                        order="4"
                        tag="h2"
                        style={{ marginBottom: "var(--g-spacing-sm)" }}
                    >
                        {name}{" "}
                        {!!nbNights &&
                            `(${nbNights} ${pluralize(
                                productPage.tripBuilder.quote.extension.durationUnit.l(),
                                nbNights,
                            )})`}
                    </Text.Heading>
                    {!!formattedPrice && (
                        <Text.Body style={{ marginTop: "var(--g-spacing-sm)" }}>
                            {formattedPrice}
                            {productPage.tripBuilder.quote.extension.perPerson.l()}
                        </Text.Body>
                    )}
                </ModalBody>
                {!!image?.filename && (
                    <StoryblokImage
                        className={styles["modal-image"]}
                        mobile={{ asset: image, size: { width: 1534 } }}
                        desktop={{ asset: image, size: { width: 996 } }}
                    />
                )}
                {!!description && (
                    <ModalBody>
                        <Markdown markdown={description} />
                    </ModalBody>
                )}
            </Modal>
        </>
    );
};
